import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { getPasswordConfirmationState } from '@/lib/passwordConfirmation'
import { cn } from '@/lib/utils'

export const RegisterPage = () => {
    const { signUp, user } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        fullName: ''
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const from = location.state?.from?.pathname || '/dashboard'

    if (user) {
        return <Navigate to={from} replace />
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.email || !formData.password || !formData.fullName) {
            setError('Veuillez remplir tous les champs')
            return
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas')
            return
        }

        if (formData.password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères')
            return
        }

        try {
            setLoading(true)
            setError('')
            const { error } = await signUp(formData.email, formData.password, {
                full_name: formData.fullName,
            })
            if (error) throw error

            // Redirect to email verification page on successful signup
            navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)
        } catch (error: any) {
            console.log("Error", error)
            //setError(error.message || 'Échec de la création du compte')
            setError('Échec de la création du compte. Veuillez contacter l\'administrateur pour vérifier que votre adresse email est autorisée.')
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const { isMismatchVisible, isConfirmed } = getPasswordConfirmationState(formData.password, formData.confirmPassword)
    const passwordClassName = "appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h4 className="mt-6 text-center text-3xl font-light text-gray-900">
                        Créer votre compte
                    </h4>
                    <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
                        DoucheFLUX - Housing First
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Vous avez déjà un compte ?{' '}
                        <Link to="/login" className="font-medium text-primary hover:text-primary/80">
                            Se connecter ici
                        </Link>
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="rounded-md bg-red-50 p-4">
                            <div className="text-sm text-red-700">{error}</div>
                        </div>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                                Nom complet
                            </label>
                            <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                required
                                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                placeholder="Jean Dupont"
                                value={formData.fullName}
                                onChange={handleChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Adresse e-mail
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                                placeholder="jean@exemple.com"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Mot de passe
                            </label>
                            <PasswordInput
                                id="password"
                                name="password"
                                autoComplete="new-password"
                                required
                                wrapperClassName="mt-1"
                                className={passwordClassName}
                                placeholder="Mot de passe (min. 6 caractères)"
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirmer le mot de passe
                            </label>
                            <PasswordInput
                                id="confirmPassword"
                                name="confirmPassword"
                                autoComplete="new-password"
                                required
                                wrapperClassName="mt-1"
                                className={cn(passwordClassName, isMismatchVisible && 'border-red-400 focus:border-red-500 focus:ring-red-500')}
                                placeholder="Confirmez votre mot de passe"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                aria-invalid={isMismatchVisible}
                                aria-describedby={isMismatchVisible ? 'password-mismatch' : undefined}
                            />
                            {isMismatchVisible && (
                                <p id="password-mismatch" className="mt-1 text-sm text-red-600">
                                    Les mots de passe ne correspondent pas
                                </p>
                            )}
                        </div>
                    </div>

                    <div>
                        <Button
                            type="submit"
                            className="group relative w-full flex justify-center"
                            disabled={loading || !isConfirmed}
                        >
                            {loading ? 'Création du compte...' : 'Créer le compte'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}